import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../../app/providers/AuthProvider';
import { cn } from '../../../utils/cn';
import { NAV_GROUPS, navFor, NavItem } from '../../../lib/navigation';
import { Avatar } from '../../ui/Display/Display';
import { IconButton } from '../../ui/IconButton/IconButton';
import { Menu, MenuItem, MenuLabel } from '../../ui/Menu/Menu';
import { Logo } from '../Logo/Logo';

const ALL_NAV = NAV_GROUPS.flatMap((g) => g.items);
const matches = (pathname: string, item: NavItem) => (item.end ? pathname === item.to : pathname === item.to || pathname.startsWith(`${item.to}/`));

/** True for the nav entry that best matches the URL (/jobs/board lights "Work board", not "Service jobs"). */
export function isActivePath(pathname: string, item: NavItem) {
  if (!matches(pathname, item)) return false;
  return !ALL_NAV.some((other) => other.to.length > item.to.length && matches(pathname, other));
}

/** Dark navigation panel. `collapsed` shrinks it to an icon rail (desktop only). */
export function Sidebar({ className, onNavigate, onClose, collapsed }: { className?: string; onNavigate?: () => void; onClose?: () => void; collapsed?: boolean }) {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const groups = navFor(user?.role);

  return (
    <aside
      className={cn('flex h-full flex-col bg-navy-800 text-white transition-[width] duration-200', collapsed ? 'w-[72px]' : 'w-64', className)}
      aria-label="Main"
    >
      <div className={cn('flex h-16 shrink-0 items-center justify-between border-b border-white/[0.08]', collapsed ? 'justify-center px-2' : 'px-5')}>
        <Link to="/" onClick={onNavigate} aria-label="Electroz home" className="rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-brand-400">
          <Logo compact={collapsed} />
        </Link>
        {onClose && <IconButton icon="x" label="Close menu" variant="onDark" size="sm" onClick={onClose} />}
      </div>

      <nav className={cn('scrollbar-none flex-1 overflow-y-auto pb-4 pt-2', collapsed ? 'px-2.5' : 'px-3')}>
        {groups.map((g, gi) => (
          <Menu key={g.label} tone="dark">
            {collapsed ? gi > 0 && <div className="mx-2 my-3 h-px bg-white/[0.08]" aria-hidden="true" /> : <MenuLabel tone="dark">{g.label}</MenuLabel>}
            {g.items.map((item) => (
              <MenuItem
                key={item.to}
                as={Link}
                to={item.to}
                tone="dark"
                icon={item.icon}
                collapsed={collapsed}
                active={isActivePath(pathname, item)}
                onClick={onNavigate}
              >
                {item.label}
              </MenuItem>
            ))}
          </Menu>
        ))}
      </nav>

      <div className={cn('flex shrink-0 items-center gap-3 border-t border-white/[0.08] py-3', collapsed ? 'flex-col px-2' : 'px-4')}>
        <Avatar name={user?.name} size={34} tone="dark" />
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{user?.name}</p>
            <p className="text-xs capitalize text-white/50">{user?.role}</p>
          </div>
        )}
        <IconButton icon="logout" label="Log out" variant="onDark" size="sm" onClick={logout} />
      </div>
    </aside>
  );
}

export default Sidebar;
